import { useEffect, useRef, useState, useCallback } from 'react'

const API_URL = process.env.REACT_APP_BACKEND_URL

// Champs = actes de gestion explicites : envoyes immediatement (le brouillon est vide avant, pour garder l'ordre)
const CHAMPS_IMMEDIATS = new Set([
  'statut', 'date_cloture', 'provision_compagnie', 'provision_source', 'gestionnaire_id', 'responsabilite_actee',
  'montant_rembourse', 'informations_importantes', 'note_gestionnaire', 'note_couleur', 'flag_attente',
  'indemnite_differee_montant', 'indemnite_differee_statut', 'indemnite_differee_date_limite',
  'indemnite_differee_montant_percu', 'indemnite_differee_date_perception', 'mode_gestion',
])

const storageKey = (id) => `pilote_brouillon_sinistre_${id}`

// Brouillon de fiche : les saisies sont gardees localement et envoyees en une fois a la sortie du dossier.
// Intercepte les PATCH /api/sinistres/{id} emis par les composants de la page (aucun composant a modifier).
export function useBrouillonSinistre(sinistreId, profile, versionRef, onFlushed) {
  const [draft, setDraft] = useState({})
  const [conflit, setConflit] = useState(null)
  const [flushing, setFlushing] = useState(false)
  const draftRef = useRef({})
  const originalFetch = useRef(null)
  const userNom = profile ? `${profile.prenom || ''} ${profile.nom || ''}`.trim() || profile.email : ''
  const metaRef = useRef({ userNom, userId: profile?.id })
  metaRef.current = { userNom, userId: profile?.id }
  const onFlushedRef = useRef(onFlushed)
  onFlushedRef.current = onFlushed

  const setDraftBoth = useCallback((next) => {
    draftRef.current = next
    setDraft(next)
    try {
      if (Object.keys(next).length) localStorage.setItem(storageKey(sinistreId), JSON.stringify({ fields: next, savedAt: Date.now() }))
      else localStorage.removeItem(storageKey(sinistreId))
    } catch { /* quota */ }
  }, [sinistreId])

  // Restauration d'un brouillon laisse par une coupure
  useEffect(() => {
    if (!sinistreId) return
    try {
      const raw = localStorage.getItem(storageKey(sinistreId))
      if (raw) {
        const parsed = JSON.parse(raw)
        if (parsed?.fields && Object.keys(parsed.fields).length) { draftRef.current = parsed.fields; setDraft(parsed.fields) }
      }
    } catch { /* ignore */ }
  }, [sinistreId])

  const envoyer = useCallback(async (payload, { keepalive = false } = {}) => {
    const fetchFn = originalFetch.current || window.fetch
    const body = { ...payload, _user_id: metaRef.current.userId, _user_nom: metaRef.current.userNom }
    if (versionRef.current) body._expected_updated_at = versionRef.current
    const res = await fetchFn(`${API_URL}/api/sinistres/${sinistreId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), keepalive,
    })
    if (res.status === 409) {
      const data = await res.clone().json().catch(() => ({}))
      if (data?.detail?.conflict) setConflit(data.detail)
    } else if (res.ok) {
      const data = await res.clone().json().catch(() => null)
      if (data?.sinistre?.updated_at) versionRef.current = data.sinistre.updated_at
    }
    return res
  }, [sinistreId, versionRef])

  // Envoi groupe du brouillon
  const flush = useCallback(async ({ keepalive = false, refresh = true } = {}) => {
    const pending = draftRef.current
    if (!Object.keys(pending).length) return true
    setFlushing(true)
    try {
      const res = await envoyer(pending, { keepalive })
      if (res.ok) {
        setDraftBoth({})
        if (refresh) onFlushedRef.current?.()
        return true
      }
      return false
    } catch {
      return false
    } finally {
      setFlushing(false)
    }
  }, [envoyer, setDraftBoth])

  const discard = useCallback(() => { setDraftBoth({}); onFlushedRef.current?.() }, [setDraftBoth])

  // Interception des PATCH de la page
  useEffect(() => {
    if (!sinistreId) return
    const target = `${API_URL}/api/sinistres/${sinistreId}`
    const original = window.fetch
    originalFetch.current = original
    window.fetch = async (input, init = {}) => {
      const url = typeof input === 'string' ? input : input?.url
      const method = (init.method || 'GET').toUpperCase()
      if (url !== target || method !== 'PATCH' || typeof init.body !== 'string') return original(input, init)
      let body
      try { body = JSON.parse(init.body) } catch { return original(input, init) }
      const fields = Object.keys(body).filter(k => !k.startsWith('_'))
      const immediat = fields.some(k => CHAMPS_IMMEDIATS.has(k))
      if (immediat) {
        if (Object.keys(draftRef.current).length) {
          const ok = await flush({ refresh: false })
          if (!ok) return new Response(JSON.stringify({ detail: 'Brouillon non enregistré' }), { status: 409, headers: { 'Content-Type': 'application/json' } })
        }
        const clean = Object.fromEntries(fields.map(k => [k, body[k]]))
        return envoyer(clean)
      }
      const next = { ...draftRef.current, ...Object.fromEntries(fields.map(k => [k, body[k]])) }
      setDraftBoth(next)
      return new Response(JSON.stringify({ success: true, brouillon: true, sinistre: next }), { status: 200, headers: { 'Content-Type': 'application/json' } })
    }
    return () => { window.fetch = original }
  }, [sinistreId, envoyer, flush, setDraftBoth])

  // Sortie du dossier : fermeture / mise en veille de l'onglet -> envoi fiable (keepalive)
  useEffect(() => {
    if (!sinistreId) return
    const onHide = () => { if (Object.keys(draftRef.current).length) flush({ keepalive: true, refresh: false }) }
    const onVisibility = () => { if (document.visibilityState === 'hidden') onHide() }
    window.addEventListener('pagehide', onHide)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('pagehide', onHide)
      document.removeEventListener('visibilitychange', onVisibility)
      onHide() // navigation interne (retour liste, autre dossier)
    }
  }, [sinistreId, flush])

  const fermerConflit = useCallback(() => setConflit(null), [])
  return { draft, draftRef, count: Object.keys(draft).length, flush, discard, flushing, conflit, fermerConflit }
}
